import { Component, type ErrorInfo, Fragment, type ReactNode } from 'react';
import { t } from '../i18n';
import { reportClientError } from './reportClientError';
import { Button } from './ui/Button';
import { cardClass } from './ui/Card';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  /** Bumped by "Try again"; keys the subtree so retrying remounts it. */
  attempt: number;
}

/**
 * The one thing standing between a single screen's render crash and a blank
 * white page with no way back. Without this, React unmounts the whole tree
 * on any uncaught render error — a null-deref on one malformed API response
 * reads as "the app is broken", not "one screen has a bug", and the only
 * recovery a person has is force-quitting and reopening.
 *
 * Must be a class component — componentDidCatch has no hook equivalent.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, attempt: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    reportClientError('error-boundary', error);
  }

  render() {
    // Keyed so that "Try again" really is a fresh attempt: clearing the error
    // alone re-renders the same component instances, and anything that had
    // already put itself into the state that crashed would crash again.
    if (!this.state.error) {
      return <Fragment key={this.state.attempt}>{this.props.children}</Fragment>;
    }

    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className={cardClass('danger', 'w-full max-w-sm space-y-3 text-center')}>
          <p className="font-heading text-lg font-bold">{t('Something went wrong')}</p>
          <p className="text-sm text-urgent/80">
            {t('This screen hit a problem. The rest of the app is fine.')}
          </p>
          {/* This used to be a full page load onto the marketing landing page,
              directly under a line promising the rest of the app was fine. It
              retries in place now; the reload is the second option, for when
              retrying does not help. */}
          <Button
            variant="danger"
            className="w-full"
            onClick={() => this.setState((s) => ({ error: null, attempt: s.attempt + 1 }))}
          >
            {t('Try again')}
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => window.location.reload()}>
            {t('Reload')}
          </Button>
        </div>
      </div>
    );
  }
}
